import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icons from "../icons";
import Loader from "./Loader";
import { DIALOG_HEIGHT } from "./dialogSize";
import ConfirmModal from "./ConfirmModal";
import { preconditionLineNote, stepToEnglish, stepToScript } from "../stepEnglish";
import { apiWsUrl } from "./GenerationView";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* Generate on Create Artifact. The server writes one Artifact per selected
   test, one test after another (a queue), from the epic's requirement file and
   the test's Cucumber scenario — no browser. This dialog follows that queue
   over the generation WebSocket, then lists what was written with Save /
   Cancel. Save hands the Inbox item to the Requirement Board review, exactly
   as the old generation tab did. */

export const NO_SCENARIO = "No Cucumber scenario on this test in Jira.";

/* Each test's circle. Filled in every state, so the colour alone reads. */
const STATE = {
  waiting: { dot: "bg-slate-300", label: "Waiting" },
  generating: { dot: "bg-amber-400 animate-pulse", label: "Creating" },
  done: { dot: "bg-emerald-500", label: "Created" },
  failed: { dot: "bg-rose-500", label: "Failed" },
  skipped: { dot: "bg-slate-400", label: "Skipped" },
};

function StateDot({ state }) {
  const s = STATE[state] || STATE.waiting;
  return (
    <span
      role="img"
      aria-label={s.label}
      title={s.label}
      className={`mt-1 h-3.5 w-3.5 flex-none rounded-full ${s.dot}`}
    />
  );
}

export const NO_ARTIFACT_STEP = "No Artifact step found.";

/* One Artifact step as the steps dialog on Artifacts shows it: what it does
   (from its action, not the AI's description) and its script line. */
function ArtifactStep({ s, n }) {
  const script = stepToScript(s);
  return (
    <li className="flex items-start gap-2 text-[0.75rem] text-slate-700">
      <span className="mt-px flex h-4 min-w-[1rem] flex-none items-center justify-center rounded bg-brand-50 px-1 text-[0.5625rem] font-semibold text-brand-700">
        {n}
      </span>
      <span className="min-w-0">
        {stepToEnglish({ ...s, description: undefined })}
        {script && <span className="mt-0.5 block break-all font-mono text-[0.625rem] text-slate-400">{script}</span>}
      </span>
    </li>
  );
}

/* What a test is written from (the `test_inputs` event): how many of its lines
   have the requirement file's rule — word for word, or matched by meaning when
   the line is worded differently — and which lines have none. */
export function inputsSummary(inputs) {
  /* An epic with work packets: the test's packet is its only source. */
  const p = inputs?.packet;
  if (p) return `Written from Packet ${p.no} of ${p.file} (${p.source || "GitHub"}) only.`;
  const r = inputs?.rules;
  if (!r) return "";
  if (!r.vocabulary) return "The requirement file has no Step vocabulary: no line has a rule.";
  const ruled = (r.exact || 0) + (r.mapped || 0);
  let text = `Rules for ${ruled} of ${r.lines} line${r.lines === 1 ? "" : "s"}`;
  if (r.mapped) text += ` (${r.mapped} matched by meaning)`;
  const missing = r.missing || [];
  if (missing.length) text += ` · no rule for line${missing.length === 1 ? "" : "s"} ${missing.join(", ")}`;
  return text;
}

/* How the run starts, as the test's Given line says (else Connect to decides). */
export function startText(start) {
  if (!start) return "";
  return `Starts signed ${start.signed_in ? "in" : "out"}, as its line ${start.line} says.`;
}

/* The rule coverage, how the run starts, and the notes when the requirement
   file is written for another epic or the test's locators are not its own
   A.2 block. Amber when a line has no rule or a note warns. */
function InputsNote({ inputs }) {
  const summary = inputsSummary(inputs);
  const start = startText(inputs?.start);
  const notes = [inputs?.file_note, inputs?.locators?.note].filter(Boolean);
  if (!summary && !start && !notes.length) return null;
  const warn = !inputs?.packet && (!inputs?.rules?.vocabulary || (inputs?.rules?.missing || []).length > 0);
  return (
    <span className="mt-0.5 block text-[0.75rem]">
      {summary && <span className={`block ${warn ? "text-amber-700" : "text-slate-500"}`}>{summary}</span>}
      {start && <span className="block text-slate-500">{start}</span>}
      {notes.map((n) => (
        <span key={n} className="block text-amber-700">
          {n}
        </span>
      ))}
    </span>
  );
}

/* A scenario line's rule as a tag: its vocabulary code ("· by meaning" when
   the line is worded differently), or "No rule". Given lines get none. */
function LineRuleTag({ inputs, stepNo }) {
  const r = inputs?.rules;
  if (!r?.vocabulary) return null;
  const hit = (r.by_line || []).find((x) => x.step_no === stepNo);
  if (hit) {
    const byMeaning = hit.how === "mapped";
    return (
      <span
        title={
          byMeaning
            ? "Worded differently from the requirement file; matched to this phrase by meaning"
            : "The requirement file's rule for this line"
        }
        className="ml-1.5 whitespace-nowrap rounded bg-brand-50 px-1.5 py-0.5 text-[0.625rem] font-semibold text-brand-700"
      >
        {hit.code}
        {byMeaning ? " · by meaning" : ""}
      </span>
    );
  }
  if ((r.missing || []).includes(stepNo)) {
    return (
      <span
        title="No phrase of the requirement file's Step vocabulary means this line"
        className="ml-1.5 whitespace-nowrap rounded bg-amber-50 px-1.5 py-0.5 text-[0.625rem] font-semibold text-amber-700"
      >
        No rule
      </span>
    );
  }
  return null;
}

/* One generated Artifact: its steps under the Cucumber line each one
   implements (source_step_no), like the steps dialog on Artifacts. */
function ArtifactCard({ test, kase, inputs }) {
  const [open, setOpen] = useState(false);
  const steps = useMemo(() => (Array.isArray(kase?.steps) ? kase.steps : []), [kase]);
  const lines = Array.isArray(kase?.xray_steps) ? kase.xray_steps : [];
  const byLine = useMemo(() => {
    const m = {};
    steps.forEach((s) => {
      const n = s.source_step_no || 0;
      (m[n] = m[n] || []).push(s);
    });
    return m;
  }, [steps]);
  const unmapped = byLine[0] || [];
  /* The steps in the order they are numbered below. */
  const ordered = [...lines.flatMap((l) => byLine[l.step_no] || []), ...unmapped];
  const preconditions = kase?.source_meta?.preconditions || [];
  let counter = 0;

  return (
    <li className="rounded-xl border border-emerald-200 bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-start gap-2.5 px-3.5 py-2.5 text-left"
      >
        <StateDot state="done" />
        <Icons.JiraXrayTest />
        <span className="min-w-0 flex-1">
          <span className="block text-[0.8125rem] font-semibold text-slate-800">{test.key}</span>
          <span className="block truncate text-[0.8125rem] text-slate-600">{test.summary}</span>
          <InputsNote inputs={inputs} />
        </span>
        <span className="flex-none rounded-full bg-emerald-50 px-2 py-0.5 text-[0.6875rem] font-semibold text-emerald-700">
          {steps.length} step{steps.length === 1 ? "" : "s"}
        </span>
        {open ? (
          <Icons.ChevronDown className="mt-0.5 h-4 w-4 flex-none text-slate-400" />
        ) : (
          <Icons.ChevronRight className="mt-0.5 h-4 w-4 flex-none text-slate-400" />
        )}
      </button>
      {open && (
        <div className="space-y-2 border-t border-slate-100 px-3.5 py-3">
          {lines.map((line) => {
            const mapped = byLine[line.step_no] || [];
            const preNote = mapped.length ? "" : preconditionLineNote(line, lines, ordered, preconditions, kase?.source_meta?.precondition_lines);
            return (
              <div key={line.step_no} className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 p-2.5">
                <p className="text-[0.75rem] text-slate-800">
                  <span className="mr-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[0.625rem] font-bold text-slate-500">
                    {line.step_no}
                  </span>
                  {line.keyword && <span className="mr-1 font-semibold text-brand-700">{line.keyword}</span>}
                  {line.action}
                  <LineRuleTag inputs={inputs} stepNo={line.step_no} />
                </p>
                <ol className="space-y-1.5">
                  {mapped.length ? (
                    mapped.map((s, i) => <ArtifactStep key={i} s={s} n={++counter} />)
                  ) : preNote ? (
                    <li className="flex items-start gap-1.5 text-[0.75rem] italic text-slate-500">
                      <Icons.JiraPrecondition className="h-4 w-4" /> {preNote}
                    </li>
                  ) : (
                    <li className="text-[0.75rem] italic text-slate-400">{NO_ARTIFACT_STEP}</li>
                  )}
                </ol>
              </div>
            );
          })}
          {unmapped.length > 0 && (
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-dashed border-slate-200 p-2.5">
              <p className="text-[0.6875rem] text-slate-400">Additional steps</p>
              <ol className="space-y-1.5">
                {unmapped.map((s, i) => (
                  <ArtifactStep key={i} s={s} n={++counter} />
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/* `variableValues` ({NAME: value}, typed at Create): Save keeps them with the
   created Artifacts, as Execute's values,
   recorded as set by `userkey`. */
export default function GenerateArtifactsDialog({ request, tests, variableValues = {}, userkey = null, onSaved, onClose }) {
  const [order, setOrder] = useState(() => tests.map((t) => ({ key: t.key, summary: t.summary || "" })));
  const [states, setStates] = useState({}); // key -> {state, message}
  const [cases, setCases] = useState({}); // key -> testcase event
  const [inputs, setInputs] = useState({}); // key -> test_inputs event (rule coverage, locators)
  /* key -> the trial gate's warnings for that Test (not Approved: its trial
     runs are not evidence), from test_started. */
  const [gateWarnings, setGateWarnings] = useState({});
  const [inboxKey, setInboxKey] = useState("");
  const [finished, setFinished] = useState(false);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const sidRef = useRef("");
  const wsRef = useRef(null);
  const finishedRef = useRef(false);

  const mark = (key, state, message = "") =>
    setStates((prev) => ({ ...prev, [key]: { state, message } }));

  const handleEvent = useCallback((ev) => {
    switch (ev.type) {
      case "queue":
        if (Array.isArray(ev.tests) && ev.tests.length) setOrder(ev.tests);
        break;
      case "test_started":
        mark(ev.key, "generating");
        if ((ev.warnings || []).length) setGateWarnings((prev) => ({ ...prev, [ev.key]: ev.warnings }));
        break;
      case "test_done":
        mark(ev.key, "done");
        break;
      case "test_skipped":
        mark(ev.key, "skipped", ev.reason || NO_SCENARIO);
        break;
      case "test_failed":
        mark(ev.key, "failed", ev.message || "Creation failed.");
        break;
      case "testcase":
        if (ev.xray_test_key) setCases((prev) => ({ ...prev, [ev.xray_test_key]: ev }));
        break;
      case "test_inputs":
        if (ev.key) setInputs((prev) => ({ ...prev, [ev.key]: ev }));
        break;
      case "saved":
        setInboxKey(ev.inbox_key || "");
        break;
      case "error":
        setError(ev.message || "Creation failed.");
        break;
      case "done":
        finishedRef.current = true;
        setFinished(true);
        break;
      default:
    }
  }, []);

  /* Start the run ONCE, then follow it. The start is kept in a ref because
     React (StrictMode, in development) mounts effects twice: starting in the
     effect itself sent two runs — two sets of AI calls and two Inbox items,
     one of which stayed pending after Save. */
  const startRef = useRef(null);
  useEffect(() => {
    let closed = false;
    if (!startRef.current) {
      startRef.current = (async () => {
        const res = await fetch(`${API_BASE_URL}features/generate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(request),
        });
        const body = await res.json().catch(() => ({}));
        const sid = body?.data?.session_id;
        if (!res.ok || !sid) throw new Error(body?.detail || `HTTP ${res.status}`);
        return sid;
      })();
    }
    /* Events read so far (frames are not counted: the server keeps every
       other event, in order, and serves the rest from that position). */
    let seen = 0;
    let polling = false;
    let watchdog = null;
    const take = (ev) => {
      if (ev.type !== "frame") seen += 1;
      handleEvent(ev);
    };
    /* The live stream did not connect, dropped, or stayed silent (a proxy
       that won't pass WebSockets): read the run's events over HTTP instead,
       so a finished run is never shown as "Waiting". */
    const poll = async (sid) => {
      if (polling) return;
      polling = true;
      try {
        wsRef.current?.close();
      } catch {
        /* ignore */
      }
      let misses = 0;
      while (!closed && !finishedRef.current) {
        try {
          const res = await fetch(`${API_BASE_URL}features/sessions/${encodeURIComponent(sid)}/events?after=${seen}`);
          const body = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
          misses = 0;
          (body.data?.events || []).forEach(take);
          if (body.data?.done && !(body.data?.events || []).length && !finishedRef.current) {
            finishedRef.current = true;
            setFinished(true);
          }
        } catch (e) {
          if (++misses >= 10) {
            setError(`Lost contact with the run: ${e.message}`);
            finishedRef.current = true;
            setFinished(true);
          }
        }
        if (!finishedRef.current) await new Promise((r) => setTimeout(r, 1000));
      }
    };
    (async () => {
      try {
        const sid = await startRef.current;
        if (closed) return;
        sidRef.current = sid;
        const ws = new WebSocket(apiWsUrl(sid));
        wsRef.current = ws;
        watchdog = setTimeout(() => {
          if (!seen) poll(sid);
        }, 5000);
        ws.onmessage = (m) => {
          if (polling) return;
          try {
            take(JSON.parse(m.data));
          } catch {
            /* not an event */
          }
        };
        ws.onclose = () => {
          if (!closed && !finishedRef.current) poll(sid);
        };
      } catch (e) {
        if (closed) return;
        setError(`Could not start generation: ${e.message}`);
        finishedRef.current = true;
        setFinished(true);
      }
    })();
    return () => {
      closed = true;
      clearTimeout(watchdog);
      try {
        wsRef.current?.close();
      } catch {
        /* ignore */
      }
    };
    // request is fixed for the dialog's life
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Not dismissible with Escape: a half-finished queue must be cancelled on
     purpose, and the Escape would otherwise reach the screen underneath. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, []);

  const stateOf = (key) => states[key]?.state || "waiting";
  const total = order.length;
  const settled = order.filter((t) => !["waiting", "generating"].includes(stateOf(t.key))).length;
  const current = order.find((t) => stateOf(t.key) === "generating");
  const generated = order.filter((t) => stateOf(t.key) === "done" && cases[t.key]);

  const cancel = async () => {
    setBusy(true);
    try {
      if (!finished && sidRef.current) {
        await fetch(`${API_BASE_URL}features/sessions/${encodeURIComponent(sidRef.current)}/cancel`, {
          method: "POST",
        }).catch(() => {});
      }
      if (inboxKey) {
        await fetch(`${API_BASE_URL}inbox/${encodeURIComponent(inboxKey)}/decline`, {
          method: "POST",
        }).catch(() => {});
      }
    } finally {
      setBusy(false);
      setConfirm(false);
      onClose?.(finished ? "Created Artifacts discarded — nothing was saved." : "Creation cancelled — nothing was saved.");
    }
  };

  /* Values typed at Create, kept with every Artifact this run created (each
     replaces the test's earlier Artifact, if any). */
  const typedValues = Object.fromEntries(
    Object.entries(variableValues || {}).filter(([, v]) => String(v || "").trim()),
  );
  const hasValues = Object.keys(typedValues).length > 0;
  const valueKeys = generated.map((t) => t.key);
  const canSave = Boolean(inboxKey) && generated.length > 0;

  /* Save saves: the user has checked the Artifacts here, so this accepts the
     run's Inbox item (the same Accept the Inbox offers), keeps the values typed
     at Create with the Artifacts, and only then goes back to the board. An item
     already accepted from the Inbox is fine, so Save can be pressed again
     after an error. */
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const accepted = await fetch(`${API_BASE_URL}inbox/${encodeURIComponent(inboxKey)}/accept`, { method: "POST" });
      const acceptBody = await accepted.json().catch(() => ({}));
      const errors = accepted.ok
        ? acceptBody?.data?.errors || []
        : /already accepted/i.test(acceptBody?.detail || "")
          ? []
          : [acceptBody?.detail || `HTTP ${accepted.status}`];
      if (errors.length) {
        setError(`Could not save: ${errors.join(" · ")}`);
        return;
      }
      if (hasValues) {
        const res = await fetch(`${API_BASE_URL}features/variable-values`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ xray_test_keys: valueKeys, values: typedValues, userkey: userkey || null }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setError(`Could not keep the values with the Artifacts: ${body?.detail || `HTTP ${res.status}`}`);
          return;
        }
      }
      onSaved?.({
        inboxKeys: [],
        xrayTestKeys: generated.map((t) => t.key),
        cases: generated.map((t) => cases[t.key]),
      });
    } catch (e) {
      setError(`Could not save: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const warned = order.filter((t) => (gateWarnings[t.key] || []).length);
  const rows = (
    <>
    {warned.length > 0 && (
      <div role="note" className="mb-2 space-y-1 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[0.75rem] text-amber-900">
        {warned.flatMap((t) =>
          gateWarnings[t.key].map((w) => (
            <p key={`${t.key}:${w}`} className="flex items-start gap-1.5">
              <Icons.Alert className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden="true" /> {w}
            </p>
          )),
        )}
      </div>
    )}
    <ul className="space-y-1.5">
      {order.map((t) => {
        const st = stateOf(t.key);
        const msg = states[t.key]?.message;
        if (finished && st === "done" && cases[t.key]) {
          return <ArtifactCard key={t.key} test={t} kase={cases[t.key]} inputs={inputs[t.key]} />;
        }
        return (
          <li
            key={t.key}
            className={[
              "flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5",
              st === "generating" ? "border-amber-200 bg-amber-50/60" : "border-slate-200 bg-card",
            ].join(" ")}
          >
            <StateDot state={st} />
            <Icons.JiraXrayTest />
            <span className="min-w-0 flex-1">
              <span className="block text-[0.8125rem] font-semibold text-slate-800">{t.key}</span>
              <span className="block truncate text-[0.8125rem] text-slate-600">{t.summary}</span>
              {msg && (
                <span
                  className={`mt-0.5 block text-[0.75rem] ${st === "failed" ? "text-rose-700" : "text-slate-500"}`}
                >
                  {msg}
                </span>
              )}
              <InputsNote inputs={inputs[t.key]} />
            </span>
            <span className="flex-none text-[0.6875rem] font-medium text-slate-500">{STATE[st].label}</span>
          </li>
        );
      })}
    </ul>
    </>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-overlay/50 backdrop-blur-sm" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="gen-artifacts-title"
        aria-busy={!finished}
        className={`relative z-10 flex ${DIALOG_HEIGHT} w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-card shadow-xl ring-1 ring-slate-200`}
      >
        <div className="flex flex-none items-center gap-2.5 bg-brand-700 px-6 py-3.5 text-white">
          {/* The Artifacts icon (as in the sidebar): these are Artifacts. */}
          <Icons.ScrollText className="h-5 w-5" />
          <h2 id="gen-artifacts-title" className="text-base font-semibold">
            {finished ? "Created Artifacts" : "Creating Artifacts"}
          </h2>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {!finished ? (
            <div className="flex flex-col items-center text-center" aria-live="polite">
              <Loader.Art variant="xray" />
              <p className="mt-3 text-base font-semibold text-slate-900">Creating artifacts…</p>
              <p className="mt-1 text-[0.8125rem] text-slate-500">
                {current
                  ? `Test ${Math.min(settled + 1, total)} of ${total} — ${current.key}`
                  : `${total} test${total === 1 ? "" : "s"} in the queue`}
              </p>
              <div className="mt-3 h-1.5 w-60 overflow-hidden rounded-full bg-slate-200">
                <div
                  className="h-1.5 rounded-full bg-brand-600 transition-all"
                  style={{ width: `${total ? Math.round((settled / total) * 100) : 0}%` }}
                />
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-700" aria-live="polite">
              {generated.length
                ? `${generated.length} of ${total} Artifact${total === 1 ? "" : "s"} generated. Open each one to check its steps, then Save to save them to their tests.`
                : "No Artifacts were generated."}
            </p>
          )}
          {error && (
            <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-rose-50 px-3 py-2 text-[0.8125rem] text-rose-700">
              <Icons.Alert className="mt-0.5 h-4 w-4 flex-none" />
              {error}
            </p>
          )}
          <div className="mt-4">{rows}</div>
        </div>

        <div className="flex flex-none justify-end gap-2 border-t border-slate-200 bg-slate-50/70 px-6 py-3">
          <button
            type="button"
            onClick={() => setConfirm(true)}
            disabled={busy || saving}
            className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-800 disabled:opacity-60"
          >
            Cancel
          </button>
          {finished && (
            <button
              type="button"
              onClick={save}
              disabled={saving || !canSave}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
            >
              <Icons.Check className="h-4 w-4" />
              Save
            </button>
          )}
        </div>
      </div>

      {saving && (
        <Loader.Overlay
          variant="guide"
          label="Saving Artifacts…"
          sublabel="Saving the generated scripts to their tests"
          delay={0}
        />
      )}

      {confirm && (
        <ConfirmModal
          title="Confirmation"
          tone="primary"
          icon={Icons.Alert}
          confirmLabel="OK"
          busy={busy}
          busyLabel="Cancelling…"
          onCancel={() => setConfirm(false)}
          onConfirm={cancel}
          message={
            finished
              ? "Are you sure, you want to discard the generated Artifacts? Nothing will be saved."
              : "Are you sure, you want to stop generating? Nothing will be saved."
          }
        />
      )}
    </div>
  );
}
